"use client";

import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function useInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isStandalone, setIsStandalone] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isAndroid, setIsAndroid] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isIOSChrome, setIsIOSChrome] = useState(false);
    const [isIOSSafari, setIsIOSSafari] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches || (window.navigator as { standalone?: boolean }).standalone;
        const userAgent = window.navigator.userAgent;
        const ios = /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const android = /Android/.test(userAgent);
        const mobile = ios || android || /Mobi|Android/i.test(userAgent);

        // Detect iOS Browser
        const isChrome = ios && /CriOS/i.test(userAgent);
        const isFirefox = ios && /FxiOS/i.test(userAgent);
        const isSafari = ios && /Safari/i.test(userAgent) && !isChrome && !isFirefox;

        setIsStandalone(!!isStandaloneMode);
        setIsIOS(ios);
        setIsAndroid(android);
        setIsMobile(mobile);
        setIsIOSChrome(isChrome);
        setIsIOSSafari(isSafari);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
        };

        window.addEventListener("beforeinstallprompt", handler);

        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
        };
    }, []);

    const promptInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === "accepted") {
                setDeferredPrompt(null);
            }
        }
    };

    return {
        deferredPrompt,
        isIOS,
        isAndroid,
        isMobile,
        isStandalone,
        promptInstall,
        isIOSChrome,
        isIOSSafari
    };
}
